/*
  La Sura - Undo after Remove in the basket ("Paths and polish" plan, basket additions).

  Removing a built cake throws away seven choices, so after a Remove the basket says
  "Removed Pistachio Rose Cake. Undo". Undo adds the same line back (variant, quantity, its
  properties and any subscription) and lets Horizon re-render the basket through its own
  cart event, exactly as an add from the product page does.

  Horizon's remove code (component-cart-items.js) is untouched. This listens in the capture
  phase, so it reads the row before Horizon removes it; the row's details come from
  data-lasura-undo, printed by cart-products.liquid (HORIZON-PATCHES.md).

  Only plain lines get Undo. A cake with extras nested under it, and the extras themselves,
  don't: re-adding them would need the nesting rebuilt, and a half-rebuilt cake is worse than
  none. The message stays until the basket changes again: no timer, so nobody is rushed.
*/
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { root, cartSections } from '@theme/lasura-basket-utils';

/** @type {{ key: string, data: object, title: string, drawer: boolean, shown: boolean } | null} */
let pending = null;

const clearMessage = () => {
  document.querySelectorAll('.lasura-undo').forEach((el) => el.remove());
};

const words = () => {
  const el = document.querySelector('.lasura-undo-text');
  return {
    removed: el?.getAttribute('data-removed') || '',
    label: el?.getAttribute('data-label') || '',
  };
};

document.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.cart-items__remove') : null;
    if (!button) return;

    clearMessage();
    pending = null;

    const row = button.closest('tr[data-key]');
    if (!(row instanceof HTMLElement) || !row.dataset.lasuraUndo) return;
    if (row.dataset.parentKey) return;
    const body = row.closest('tbody');
    if (body?.querySelector(`tr[data-parent-key="${CSS.escape(row.dataset.key || '')}"]`)) return;

    try {
      pending = {
        key: row.dataset.key || '',
        data: JSON.parse(row.dataset.lasuraUndo),
        title: row.dataset.lasuraTitle || '',
        drawer: Boolean(button.closest('cart-drawer-component')),
        shown: false,
      };
    } catch {
      pending = null;
    }
  },
  true
);

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const source = /** @type {any} */ (event).detail?.data?.source;
  if (source === 'lasura-undo') return;

  if (pending && !pending.shown) {
    /* Only once the basket confirms the line has gone: a Remove that failed, or an update from
       somewhere else, must not offer Undo for a line that's still there. */
    const detail = /** @type {any} */ (event).detail;
    const items = detail?.resource?.items;
    const removed =
      source === 'cart-items-component' &&
      !detail?.data?.didError &&
      Array.isArray(items) &&
      !items.some((item) => item.key === pending?.key);
    if (removed) {
      pending.shown = true;
      const current = pending;
      /* Horizon morphs the basket right after this event; show the message once that's done. */
      requestAnimationFrame(() => requestAnimationFrame(() => showMessage(current)));
      return;
    }
    pending = null;
  }

  /* A change from elsewhere (an add from a product page, say) ends the chance to undo. Horizon's
     own follow-up updates from the basket don't, or the message would vanish as it appears. */
  if (source === 'cart-items-component') return;
  clearMessage();
  pending = null;
});

/** @param {{ data: object, title: string, drawer: boolean }} removed */
function showMessage(removed) {
  const { removed: template, label } = words();
  if (!template || !label) return;

  const hosts = Array.from(document.querySelectorAll('cart-items-component'));
  const host = hosts.find((el) => Boolean(el.closest('cart-drawer-component')) === removed.drawer) || hosts[0];
  if (!host) return;

  const message = document.createElement('div');
  message.className = 'lasura-undo';
  message.setAttribute('role', 'status');

  const text = document.createElement('span');
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'lasura-undo__button';
  button.textContent = label;
  button.addEventListener('click', () => undo(removed, button));

  message.append(text, ' ', button);
  host.prepend(message);
  /* Filled a frame later, so screen readers announce it as a change. */
  requestAnimationFrame(() => {
    text.textContent = template.replace('{title}', removed.title);
  });
}

/**
 * @param {{ data: object }} removed
 * @param {HTMLButtonElement} button
 */
async function undo(removed, button) {
  button.disabled = true;
  try {
    const sections = cartSections();
    const response = await fetch(
      `${root()}cart/add.js`,
      fetchConfig('json', {
        body: JSON.stringify({
          items: [removed.data],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      })
    );
    const added = await response.json();
    if (!response.ok || added.status) throw new Error(added.description || 'Could not add it back');

    const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
    clearMessage();
    pending = null;
    document.dispatchEvent(
      new CartAddEvent(cart, 'lasura-undo', {
        source: 'lasura-undo',
        itemCount: cart.item_count,
        sections: added.sections,
      })
    );
  } catch (error) {
    console.error(error);
    button.disabled = false;
  }
}
